// JourneyTimelineCard — visualises the call as a series of stages
// (Opener → Discovery → Recommendation → Objection → Close → Follow-up)
// with a green/red tick indicating whether the corresponding criterion
// was demonstrated. Uses the same `criteria_results[]` payload the
// grader already emits — no new backend work required.
//
// Mapping (criterion.description substring → stage):
//   opener / greeting                     → Opener
//   discovery / green brain / motivation  → Discovery
//   active listening / alf                → Active Listening
//   disc / adapt                          → DISC Adapt
//   link / recommendation / suggest       → Recommendation
//   price / rule of three                 → Price
//   objection                             → Objection
//   close / alternate                     → Close
//   follow-up / follow up / commitment    → Follow-up
//
// Falls back to a "General coaching" bucket for anything that doesn't
// match. Not-applicable-for-type criteria are excluded.
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Route, CheckCircle2, XCircle, MinusCircle, Clock } from 'lucide-react';

const STAGE_ORDER = [
  { key: 'opener',        label: 'Opener' },
  { key: 'discovery',     label: 'Discovery' },
  { key: 'active',        label: 'Listen' },
  { key: 'disc',          label: 'DISC' },
  { key: 'recommend',     label: 'Recommend' },
  { key: 'price',         label: 'Price' },
  { key: 'objection',     label: 'Objection' },
  { key: 'close',         label: 'Close' },
  { key: 'follow_up',     label: 'Follow-up' },
];

const bucketFor = (description) => {
  const d = (description || '').toLowerCase();
  if (/(opener|greeting|introduc)/.test(d)) return 'opener';
  if (/(discov|green brain|motivation|need)/.test(d)) return 'discovery';
  if (/(active listen|alf|formulat)/.test(d)) return 'active';
  if (/(disc|adapt)/.test(d)) return 'disc';
  if (/(recommend|link|suggest|solution)/.test(d)) return 'recommend';
  if (/(price|rule of three|value)/.test(d)) return 'price';
  if (/(objection)/.test(d)) return 'objection';
  if (/(close|alternate|book)/.test(d)) return 'close';
  if (/(follow.?up|commit|next step)/.test(d)) return 'follow_up';
  return null;
};

const StageDot = ({ state, label }) => {
  const tone = {
    met:      { ring: 'ring-emerald-400 bg-emerald-500', text: 'text-emerald-700', Icon: CheckCircle2 },
    missed:   { ring: 'ring-rose-400 bg-rose-500',       text: 'text-rose-700',    Icon: XCircle },
    notNeeded:{ ring: 'ring-amber-300 bg-amber-400',     text: 'text-amber-700',   Icon: MinusCircle },
    empty:    { ring: 'ring-slate-200 bg-slate-100',     text: 'text-slate-400',   Icon: Clock },
  }[state] || {};
  const Icon = tone.Icon;
  return (
    <div className="flex flex-col items-center min-w-[64px]" data-testid={`journey-stage-${label.toLowerCase().replace(/\s|-/g, '-')}`}>
      <div className={`w-9 h-9 rounded-full ring-4 ${tone.ring} flex items-center justify-center shadow-sm`}>
        {Icon && <Icon className="w-5 h-5 text-white" />}
      </div>
      <span className={`text-[10px] font-semibold uppercase tracking-wide mt-1 ${tone.text}`}>{label}</span>
    </div>
  );
};

const JourneyTimelineCard = ({ grading }) => {
  const stageState = useMemo(() => {
    const state = Object.fromEntries(STAGE_ORDER.map(s => [s.key, 'empty']));
    const crits = (grading?.criteria_results || []).filter(c => !c.not_applicable_for_type);
    crits.forEach((c) => {
      const bucket = bucketFor(c.description);
      if (!bucket) return;
      // "not_needed_for_win" wins over met/missed for display purposes.
      const next = c.not_needed_for_win ? 'notNeeded' : (c.met ? 'met' : 'missed');
      // Prefer worst-status if multiple criteria map to the same stage,
      // so a missed-then-met combo doesn't turn green optimistically.
      const rank = { missed: 3, notNeeded: 2, met: 1, empty: 0 };
      if (rank[next] > rank[state[bucket]]) state[bucket] = next;
    });
    return state;
  }, [grading]);

  if (!grading) return null;

  const hits = Object.values(stageState).filter(s => s === 'met').length;
  const misses = Object.values(stageState).filter(s => s === 'missed').length;
  const covered = STAGE_ORDER.length - Object.values(stageState).filter(s => s === 'empty').length;

  return (
    <Card className="border-slate-200" data-testid="journey-timeline-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Route className="w-4 h-4 text-teal-600" /> Journey timeline
          <span className="text-xs text-slate-400 font-normal ml-1">
            · {hits} hit · {misses} missed · {covered}/{STAGE_ORDER.length} stages covered
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <div className="absolute top-4 left-6 right-6 h-0.5 bg-gradient-to-r from-slate-200 via-slate-200 to-slate-200" />
          <div className="flex items-start justify-between gap-1 overflow-x-auto pb-1 relative">
            {STAGE_ORDER.map(s => (
              <StageDot key={s.key} state={stageState[s.key]} label={s.label} />
            ))}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-center gap-4 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Hit</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500" />Missed</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />Not needed</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300" />N/A</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default JourneyTimelineCard;
